import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { ldapAPI } from '../services/api';

function LDAP() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [browseData, setBrowseData] = useState(null);

  // Search state
  const [searchParams, setSearchParams] = useState({
    base_dn: '',
    filter: '(objectClass=*)',
    attributes: '',
  });

  // Browse state
  const [baseDn, setBaseDn] = useState('');

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        base_dn: searchParams.base_dn,
        filter: searchParams.filter,
      };
      if (searchParams.attributes) {
        params.attributes = searchParams.attributes.split(',').map((a) => a.trim());
      }
      const response = await ldapAPI.search(params);
      setSearchResults(response.data.results || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBrowse = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ldapAPI.browse(baseDn);
      setBrowseData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Browse failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        base_dn: searchParams.base_dn,
        filter: searchParams.filter,
      };
      const response = await ldapAPI.export(params);

      const blob = new Blob([response.data.ldif], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export.ldif';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Export failed');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        LDAP Browser
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Search" />
          <Tab label="Browse" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Base DN"
                  value={searchParams.base_dn}
                  onChange={(e) =>
                    setSearchParams({ ...searchParams, base_dn: e.target.value })
                  }
                  helperText="Example: dc=example,dc=com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Filter"
                  value={searchParams.filter}
                  onChange={(e) =>
                    setSearchParams({ ...searchParams, filter: e.target.value })
                  }
                  helperText="Example: (objectClass=user) or (&(objectClass=user)(sAMAccountName=john))"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Attributes (optional)"
                  value={searchParams.attributes}
                  onChange={(e) =>
                    setSearchParams({ ...searchParams, attributes: e.target.value })
                  }
                  helperText="Comma-separated list. Leave empty for all attributes."
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Search'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    disabled={loading || searchResults.length === 0}
                  >
                    Export to LDIF
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {searchResults.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Results ({searchResults.length} entries)
                </Typography>
                {searchResults.map((entry, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>{entry.dn}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Attribute</TableCell>
                              <TableCell>Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(entry.attributes || {}).map(
                              ([key, value]) => (
                                <TableRow key={key}>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {key}
                                  </TableCell>
                                  <TableCell>
                                    {Array.isArray(value)
                                      ? value.join(', ')
                                      : value}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Base DN"
                  value={baseDn}
                  onChange={(e) => setBaseDn(e.target.value)}
                  helperText="Browse from this DN"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleBrowse}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Browse'}
                </Button>
              </Grid>
            </Grid>

            {browseData && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Browse Results
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>DN</TableCell>
                        <TableCell>Object Class</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(browseData.entries || []).map((entry, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {entry.dn}
                          </TableCell>
                          <TableCell>
                            {entry.objectClass || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default LDAP;
